import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { App, Button, Space } from 'antd';

import './AppVersionChecker.less';

const NOTICE_KEY = 'app-version-update';
const RUNNING_VERSION = typeof __APP_BUILD_VERSION__ !== 'undefined' ? __APP_BUILD_VERSION__ : '';

async function fetchRemoteVersion() {
    const response = await fetch(`/version.json?_t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) {
        return null;
    }
    const data = await response.json();
    return data?.version ? String(data.version) : null;
}

export default function AppVersionChecker() {
    const location = useLocation();
    const { notification } = App.useApp();
    const snoozedVersionRef = useRef(null);
    const checkingRef = useRef(false);

    const showUpdateNotice = useCallback(
        latestVersion => {
            if (snoozedVersionRef.current === latestVersion) {
                return;
            }

            notification.open({
                key: NOTICE_KEY,
                message: '发现新版本',
                description: '请保存当前工作后再刷新页面，避免数据丢失',
                duration: 0,
                placement: 'bottomLeft',
                className: 'app-version-notice',
                icon: (
                    <span className="app-version-notice-icon" aria-hidden="true">
                        <i className="bi bi-arrow-repeat" />
                    </span>
                ),
                btn: (
                    <Space>
                        <Button
                            onClick={() => {
                                snoozedVersionRef.current = latestVersion;
                                notification.destroy(NOTICE_KEY);
                            }}
                        >
                            稍后更新
                        </Button>
                        <Button type="primary" onClick={() => window.location.reload()}>
                            立即更新
                        </Button>
                    </Space>
                ),
            });
        },
        [notification],
    );

    const checkVersion = useCallback(async () => {
        // 调试样式临时逻辑，调试完成后恢复版本对比逻辑
        // showUpdateNotice('debug-version');
        // return;

        // eslint-disable-next-line no-unreachable
        if (!RUNNING_VERSION || checkingRef.current) {
            return;
        }

        checkingRef.current = true;
        try {
            const latestVersion = await fetchRemoteVersion();
            if (!latestVersion || latestVersion === RUNNING_VERSION) {
                return;
            }
            showUpdateNotice(latestVersion);
        } catch (err) {
            console.log('版本检查失败', err);
        } finally {
            checkingRef.current = false;
        }
    }, [showUpdateNotice]);

    useEffect(() => {
        const timer = setTimeout(checkVersion, 100);
        return () => clearTimeout(timer);
    }, [location.pathname, checkVersion]);

    return null;
}
